import { Fragment } from 'react';
import { Ban, Reply, Trash2 } from 'lucide-react';
import clsx from 'clsx';

import Avatar from '@/components/Avatar';
import { ReplyQuote } from '@/components/chat/ReplyQuote';
import VoicePlayer from '@/components/chat/VoicePlayer';
import { useSwipeReply } from '@/components/chat/useSwipeReply';
import type { ChatMessage } from '@/lib/chat';
import { mediaUrl } from '@/lib/media';
import { ROLE_LABEL } from '@/lib/sales';

const TIME = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' });
const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,:;"')\]!?])/g;

/** Ссылки в тексте кликабельны, остальное — как есть (переносы строк сохраняет CSS). */
function Linkified({ text, own }: { text: string; own: boolean }) {
  const parts = text.split(URL_RE);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noreferrer"
            className={clsx('break-all underline underline-offset-2', own ? 'text-white' : 'text-orange-700')}
          >
            {part}
          </a>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}

/** Превью фото в ленте: пропорции известны заранее — лента не прыгает, пока грузится. */
function PhotoPreview({ url, width, height, onOpen }: { url: string; width: number | null; height: number | null; onOpen: () => void }) {
  const ratio = width && height ? Math.min(1.9, Math.max(0.56, width / height)) : 4 / 3;
  return (
    <button type="button" onClick={onOpen} className="block w-full overflow-hidden rounded-xl bg-ink/5" aria-label="Открыть фото">
      <img
        src={mediaUrl(url)}
        alt=""
        loading="lazy"
        decoding="async"
        className="block w-full object-cover"
        style={{ aspectRatio: ratio }}
      />
    </button>
  );
}

export default function MessageBubble({
  message: m,
  own,
  grouped,
  highlighted,
  onReply,
  onDelete,
  onOpenPhoto,
  onOpenReply,
}: {
  message: ChatMessage;
  own: boolean;
  /** Продолжение серии одного автора: без имени и аватара. */
  grouped: boolean;
  /** Подсветка после перехода по цитате. */
  highlighted: boolean;
  /** Нет — ответить нельзя: сообщение удалено. */
  onReply?: () => void;
  /** Нет — удалить нельзя: удаляют только своё. */
  onDelete?: () => void;
  onOpenPhoto: (url: string) => void;
  onOpenReply: (id: string) => void;
}) {
  const swipe = useSwipeReply(onReply);
  const deleted = !!m.deleted;
  const a = deleted ? null : (m.attachment ?? null);
  const reply = deleted ? null : (m.replyTo ?? null);
  const hasText = !deleted && m.text.trim().length > 0;

  return (
    <div
      data-msg-id={m.id}
      className={clsx(
        'rounded-2xl transition-colors',
        grouped ? 'mt-0.5' : 'mt-3',
        highlighted ? 'bg-orange/20 duration-150' : 'duration-700',
        onReply && 'touch-pan-y touch-pinch-zoom',
      )}
      {...swipe.handlers}
    >
      <div ref={swipe.rowRef} className={clsx('group flex items-start gap-2', own ? 'justify-end' : 'justify-start')}>
        {!own ? (
          <div className="w-8 shrink-0">
            {!grouped ? <Avatar name={m.author.name} url={m.author.avatarUrl} size={32} className="mt-5" /> : null}
          </div>
        ) : null}
        <div className={clsx('relative flex min-w-0 max-w-[80%] flex-col sm:max-w-[70%]', own ? 'items-end' : 'items-start')}>
          {onReply ? (
            // Проявляется слева, пока сообщение тянут вправо (useSwipeReply).
            <span
              ref={swipe.iconRef}
              aria-hidden
              className={clsx(
                'pointer-events-none absolute top-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-ink/[0.07] text-ink/60 opacity-0',
                own ? 'right-[calc(100%+8px)]' : 'right-[calc(100%+48px)]',
              )}
              style={{ transform: 'translateY(-50%) scale(0.5)' }}
            >
              <Reply size={16} />
            </span>
          ) : null}
          {!own && !grouped ? (
            <div className="mb-1 flex items-center gap-1.5 px-1 text-xs">
              <span className="font-medium text-ink/75">{m.author.name}</span>
              {m.author.role !== 'MANAGER' ? (
                <span className="rounded bg-orange/10 px-1 py-px text-[10px] font-medium text-orange-700">
                  {ROLE_LABEL[m.author.role]}
                </span>
              ) : null}
            </div>
          ) : null}
          <div className={clsx('flex max-w-full items-end gap-1.5', own && 'flex-row-reverse')}>
            {deleted ? (
              <div
                className={clsx(
                  'flex items-center gap-1.5 rounded-2xl px-3.5 py-2 text-[15px] italic leading-snug text-ink/45 ring-1 ring-line/70',
                  own ? 'rounded-br-md bg-white/50' : 'rounded-bl-md bg-white/70',
                )}
              >
                <Ban size={14} className="shrink-0" aria-hidden />
                Сообщение удалено
              </div>
            ) : (
              <div
                className={clsx(
                  'min-w-0 rounded-2xl text-[15px] leading-snug',
                  own ? 'rounded-br-md bg-orange text-white' : 'rounded-bl-md bg-white text-ink shadow-sm ring-1 ring-line/60',
                  // Подпись переносится по ширине фото или плеера, а не раздувает пузырь.
                  a?.type === 'photo'
                    ? 'w-[min(268px,calc(64vw+8px))] max-w-full p-1'
                    : a?.type === 'voice'
                      ? 'max-w-[256px] px-2.5 py-2'
                      : 'max-w-full px-3.5 py-2',
                )}
              >
                {reply ? (
                  <div className={a?.type === 'photo' ? 'mb-1' : a?.type === 'voice' ? 'mb-1.5' : '-mx-1.5 -mt-0.5 mb-1.5'}>
                    <ReplyQuote reply={reply} own={own} onOpen={() => onOpenReply(reply.id)} />
                  </div>
                ) : null}
                {a?.type === 'photo' ? (
                  <PhotoPreview url={a.url} width={a.width} height={a.height} onOpen={() => onOpenPhoto(a.url)} />
                ) : null}
                {a?.type === 'voice' ? (
                  <VoicePlayer url={a.url} durationMs={a.durationMs} waveform={a.waveform} own={own} />
                ) : null}
                {hasText ? (
                  <div
                    className={clsx(
                      'whitespace-pre-wrap break-words',
                      a && '[overflow-wrap:anywhere]',
                      a?.type === 'photo' && 'px-2.5 pb-1.5 pt-1.5',
                      a?.type === 'voice' && 'px-1 pt-1.5',
                    )}
                  >
                    <Linkified text={m.text} own={own} />
                  </div>
                ) : null}
              </div>
            )}
            <div className="flex shrink-0 flex-col items-center pb-0.5">
              {onReply || onDelete ? (
                <div className="flex items-center">
                  {onReply ? (
                    // На тач-экранах отвечают свайпом — кнопка только для мыши.
                    <button
                      type="button"
                      onClick={onReply}
                      className="hidden rounded-md p-1 text-ink/35 opacity-0 transition-opacity hover:text-ink/70 focus-visible:opacity-100 group-hover:opacity-100 [@media(pointer:fine)]:block"
                      aria-label="Ответить"
                    >
                      <Reply size={13} aria-hidden />
                    </button>
                  ) : null}
                  {onDelete ? (
                    <button
                      type="button"
                      onClick={onDelete}
                      className="rounded-md p-1 text-ink/35 opacity-0 transition-opacity hover:text-rose-600 focus-visible:opacity-100 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-60"
                      aria-label="Удалить сообщение"
                    >
                      <Trash2 size={13} aria-hidden />
                    </button>
                  ) : null}
                </div>
              ) : null}
              <span className="text-[10px] tabular-nums text-ink/40">{TIME.format(new Date(m.createdAt))}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
